import React, { useState } from 'react'
import { Container, Typography, Grid2, Link, Skeleton, Paper, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, Checkbox, FormGroup, FormHelperText, Alert } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { IconChevronLeft } from '@tabler/icons-react'
import { useParams, useNavigate } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form';
import axios from "axios";
import { useMutation, useQuery } from "@tanstack/react-query";

import CustomButton from '@components/button/customButton'
import ControlledTextField from '@components/inputs/controlledTextField'
import ControlledAutocomplete from '@components/inputs/controlledAutocomplete'
import CustomTextField from '@components/inputs/customTextField'
import ConfirmDialog from '@components/dialog/confirmDialog'
import InfoDialog from '@components/dialog/infoDialog'

import { useSnackbar } from '@context/snackbarContext'

import { UserApiEndpoints } from '@configuration/apiEndpoints'

import { EVENT_STATUS_GUID, PAYMENT_TERM } from '@utils/constant'

import "./application.css";


const transformData = (data, formQuestions) => {
  let result = [];
  
  // Process Single Answer
  if (data["Single Answer"]) {
    Object.entries(data["Single Answer"]).forEach(([questionGuid, answer]) => {
      const { question, data } = formQuestions.find(question => question.question.questionGuid === questionGuid);
      result.push({ questionGuid, question: question.questionEng, answer, remark: "", data: data || null });
    });
  }

  // Process Multi-Option & Single Choice
  if (data["Multi-Option & Single Choice"]) {
    Object.entries(data["Multi-Option & Single Choice"]).forEach(([questionGuid, { selectedOptionGuid, subTextValue }]) => {
      const { question, data  } = formQuestions.find(question => question.question.questionGuid === questionGuid);
      const selectedAnswer = question?.questionOptions.find(option => option.questionOptionGuid === selectedOptionGuid)
      result.push({ questionGuid, question: question.questionEng, answer: selectedAnswer.optionEng, remark: subTextValue, data: data || null });
    });
  }

  // Process Multi-Option & Multi-Choice
  if (data["Multi-Option & Multi-Choice"]) {
    Object.entries(data["Multi-Option & Multi-Choice"]).forEach(([questionGuid, answers]) => {
      const { question } = formQuestions.find(question => question.question.questionGuid === questionGuid);
      answers.forEach(answer => {
        const selectedAnswer = question?.questionOptions.find(option => option.questionOptionGuid === answer)
        result.push({ questionGuid, question: question.questionEng, answer: selectedAnswer.optionEng, remark: "", data: null });
      });
    });
  }

  return result;
};


export default function Application() {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const openSnackbar = useSnackbar();

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)
  const [openInfoDialog, setOpenInfoDialog] = useState(false)

  const { handleSubmit, control, getValues, reset, } = useForm({})

  const { isFetching, data, isError } = useQuery({
    queryKey: ['getActivityRegistrationForm'],
    queryFn: () => { 
      return axios.get(`${UserApiEndpoints.getActivityRegistrationForm}/${id}`, { requiresAuth: false })
    },
    initialData: null,
    select: (res) => {
      const data = res.data
      data.form?.formQuestions?.sort((a, b) => a.sequence - b.sequence);
      data.postFeeOptions?.sort((a, b) => a.sequence - b.sequence);
      data.form?.formQuestions?.forEach(q => {
        const isMultiOptionSingleChoice = q.question.questionType?.valueEng === "Multi-Option & Single Choice";
      
        if (isMultiOptionSingleChoice && Array.isArray(q.question.questionOptions)) {
          q.question.questionOptions.sort((a, b) => {
            // Place subText: true at the bottom
            return (a.subText === b.subText) ? 0 : a.subText ? 1 : -1;
          });
        }
      });
      return data || null
    },
  })

  const registerMutation = useMutation({
    mutationFn: (data) => {
      return axios.post(UserApiEndpoints.customerRegistration, data, { requiresAuth: false })
    },
    onSuccess: (res) => {
      if(data?.paymentTerm === PAYMENT_TERM.PREPAYMENT){
        navigate(`/payment/application/${res.data?.applicationGuid}`, { replace: true });
      }
      else{
        setOpenConfirmDialog(false)
        setOpenInfoDialog(true)
      }
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      openSnackbar(t('register-page.registerFailed'), "error");
    }
  })

  const onSubmit = () => {
    setOpenConfirmDialog(true)
  };

  const onConfirmSubmit = () => {
    const submitData = getValues()
    const formData = {
      ...submitData,
      customer: {
        ...submitData.customer,
        sex: submitData?.customer?.sex?.value
      },
      formAnswer: transformData(submitData.formAnswer, data?.form?.formQuestions),
      formGuid: data?.form?.formGuid,
      postGuid: data?.postGuid
    }
    registerMutation.mutate(formData)
  }

  const onBack = () => {
    navigate(`/activity/${id}`, { replace: true });
  }

  const isSkeleton = !(data);

  const disabledSubmit = (data?.eventStatus?.parameterGuid !== EVENT_STATUS_GUID.OPENED || !data?.isRegister)
  
  return (
    <Container className='activity-application'>
      <Link onClick={onBack} underline="hover" color='#414042' fontSize={'14px'} style={{display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer'}}>
        <IconChevronLeft size={15}/>{t('activity-page.back')}
      </Link>
      <Typography className='category'>{isSkeleton ? <Skeleton animation={!isError && 'pulse'} variant='text'/> : t(`activity-page.${data?.category?.valueEng}`)}</Typography>
      <Typography className='title'>{isSkeleton ? <Skeleton animation={!isError && 'pulse'} variant='text'/> : data?.[`title${t('param')}`]}</Typography>
      <Typography className='description'>{isSkeleton ? <Skeleton animation={!isError && 'pulse'} variant='text'/> : t('register-page.fillInstruction') }</Typography>
      {isSkeleton ?
        <Skeleton animation={!isError && 'pulse'} variant="rounded" height={"400px"} style={{borderRadius: '10px'}}/>
      :
        <form onSubmit={handleSubmit(onSubmit)} className='content-wrapper'>
          {disabledSubmit &&
            <Alert severity="error">
              {
                !data?.isRegister ? 
                  t('register-page.noRegistrationAlert') : 
                [EVENT_STATUS_GUID.NEW, EVENT_STATUS_GUID.POSTED].includes(data?.eventStatus?.parameterGuid ?? '') ?
                  t('register-page.notOpenAlert') : 
                [EVENT_STATUS_GUID.CLOSED].includes(data?.eventStatus?.parameterGuid ?? '') ?
                  t('register-page.registrationOverAlert') :
                [EVENT_STATUS_GUID.ENDED, EVENT_STATUS_GUID.VOIDED].includes(data?.eventStatus?.parameterGuid ?? '') ? 
                  t('register-page.endedAlert') : null
              }
            </Alert>
          }
          <Paper className='content-card'>
            <Grid2 container spacing={'25px'}>
              <Grid2 size={12}>
                <Grid2 size={{ lg: 6, md: 6,  sm: 8, xs: 12 }}>
                  <ControlledTextField 
                    name="customer.customerName"
                    control={control}
                    label={t('register-page.fullName')}
                    variant='standard'
                    size='medium'
                    rules={{
                      required: t('register-page.fullNameRequired'),
                      pattern: {
                        value: /^[A-Za-z\s'-]+$/, // ✅ Only A-Z, a-z, '-, and spaces
                        message: t('register-page.fullNameLettersOnly'),
                      },
                    }}
                    // disabled={fetchFundMutation.isPending || fetchFundMutation.isError}
                  />
                </Grid2>
              </Grid2>
              <Grid2 size={12}>
                <Grid2 size={{ lg: 6, md: 6,  sm: 8, xs: 12 }}>
                  <ControlledTextField 
                    name="customer.age"
                    control={control}
                    label={t('register-page.age')}
                    type='number'
                    variant='standard'
                    size='medium'
                    rules={{
                      required: t('register-page.ageRequired'),
                      min: {
                        value: 0,
                        message: t('register-page.ageInvalid')
                      },
                      max: {
                        value: 120,
                        message: t('register-page.ageInvalid')
                      },
                      valueAsNumber: true // Ensures the value is treated as a number
                    }}
                    // disabled={fetchFundMutation.isPending || fetchFundMutation.isError}
                  />
                </Grid2>
              </Grid2>
              <Grid2 size={12}>
                <Grid2 size={{ lg: 6, md: 6,  sm: 8, xs: 12 }}>
                  <ControlledAutocomplete 
                    name="customer.sex"
                    control={control}
                    label={t('register-page.gender')}
                    variant='standard'
                    size='medium'
                    options={[
                      {value: 'Male', label: t('register-page.male')},
                      {value: 'Female', label: t('register-page.female')},
                    ]}
                    rules={{
                      required: t('register-page.genderRequired')
                    }}
                    // disabled={fetchFundMutation.isPending || fetchFundMutation.isError}
                  />
                </Grid2>
              </Grid2>
              <Grid2 size={12}>
                <Grid2 size={{ lg: 6, md: 6,  sm: 8, xs: 12 }}>
                  <ControlledTextField 
                    name="customer.emailAddress"
                    control={control}
                    label={t('register-page.emailAddress')}
                    variant='standard'
                    size='medium'
                    rules={{
                      required: t('register-page.emailAddressRequired'),
                      pattern: {
                        value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                        message: t('register-page.emailAddressInvalid'),
                      }
                    }}
                    // disabled={fetchFundMutation.isPending || fetchFundMutation.isError}
                  />
                </Grid2>
              </Grid2>
              <Grid2 size={12}>
                <Grid2 size={{ lg: 6, md: 6,  sm: 8, xs: 12 }}>
                  <ControlledTextField 
                    name="customer.contactNumber"
                    control={control}
                    label={t('register-page.contactNumber')}
                    variant='standard'
                    size='medium'
                    rules={{
                      required: t('register-page.contactNumberRequired'),
                      pattern: {
                        value: /^[0-9]+$/,
                        message: t('register-page.contactNumberInvalid')
                      }
                    }}
                    // disabled={fetchFundMutation.isPending || fetchFundMutation.isError}
                  />
                </Grid2>
              </Grid2>
            </Grid2>
          </Paper>
          {data?.form?.formQuestions && 
          <Paper className='content-card'>
            <Grid2 container spacing={'25px'}>
            {/* Dynamic fields */}
            {data?.form?.formQuestions && 
              data?.form?.formQuestions
              .map((item, index) => (
              <Grid2 size={12} key={index}>
                <Grid2 size={{ lg: 6, md: 6,  sm: 8, xs: 12 }}>
                {item?.question?.questionType?.valueEng === "Single Answer" && (
                  <ControlledTextField 
                    name={`formAnswer.Single Answer.${item.questionGuid}`}
                    control={control}
                    label={item.question[`question${t('param')}`]}
                    variant='standard'
                    size='medium'
                    rules={{
                      required: t('register-page.answerRequired')
                    }}
                  />
                )}

                {item?.question?.questionType?.valueEng === "Multi-Option & Multi-Choice" && (
                  <Controller
                    name={`formAnswer.Multi-Option & Multi-Choice.${item.questionGuid}`}
                    control={control}
                    defaultValue={[]}
                    rules={{
                      validate: (value) => value.length === 0 ? t('register-page.answerRequired')
                                          : value.length > item?.question?.maxAllowed ? t('register-page.selectMaxAllowedOnly', {maxAllowed: item?.question?.maxAllowed}) : true,
                      setValueAs: (value) => value.split(","),
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <FormControl
                        error={error}
                        fullWidth
                      >
                        <FormLabel component="legend" style={{fontSize: '12px'}}>{item.question[`question${t('param')}`]}</FormLabel>
                        <FormGroup>
                          {item?.question?.questionOptions.map((choice, index) => (
                            <FormControlLabel
                              key={index}
                              control={
                                <Checkbox
                                  size='small'
                                  checked={field.value.includes(choice.questionOptionGuid)}
                                  onChange={(e) => {
                                    const isChecked = e.target.checked;
                                    const currentValue = field.value;
                                    const updatedValue = isChecked
                                      ? [...currentValue, choice.questionOptionGuid]
                                      : currentValue.filter((opt) => opt !== choice.questionOptionGuid);

                                    field.onChange(updatedValue);
                                  }}
                                />
                              }
                              label={<Typography fontSize={'14px'}>{choice[`option${t('param')}`]}</Typography>}
                            />
                          ))}
                        </FormGroup>
                        {error &&
                          <FormHelperText variant='standard'>{error.message}</FormHelperText>
                        }
                      </FormControl>
                    )}
                  />
                )}

                {item?.question?.questionType?.valueEng === "Multi-Option & Single Choice" && (
                  <Controller
                    name={`formAnswer.Multi-Option & Single Choice.${item.questionGuid}`}
                    control={control}
                    defaultValue={{ selectedOptionGuid: "", subTextValue: "", subText: false }}
                    rules={{
                      validate: (value) => {
                        if (value.selectedOptionGuid === "") {
                          return t('register-page.answerRequired');
                        }
                        const selectedChoice = item.question?.questionOptions.find(choice => choice.questionOptionGuid === value.selectedOptionGuid);
                        if (selectedChoice?.subText && !value.subTextValue.trim()) {
                          return t('register-page.pleaseEnterAnswer');
                        }
                        return true;
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <FormControl
                        error={error}
                        fullWidth
                      >
                        <FormLabel component="legend" style={{fontSize: '12px'}}>{item.question[`question${t('param')}`]}</FormLabel>
                        <RadioGroup
                          value={field.value?.selectedOptionGuid}
                          onChange={(e) => {
                            const selectedOptionGuid = e.target.value;
                            const selectedChoice = item.question?.questionOptions.find(choice => choice.questionOptionGuid === selectedOptionGuid);
                            field.onChange({
                              ...field.value,
                              selectedOptionGuid,
                              subText: selectedChoice?.subText || false, // Update subText based on the selected option
                              subTextValue: selectedChoice?.subText ? field.value.subTextValue : "", // Clear subTextValue if subText is not needed
                            });
                          }}
                        >
                          {item?.question?.questionOptions
                            .map((choice, index) => (
                            <div key={index}>
                              <FormControlLabel
                                value={choice.questionOptionGuid}
                                control={<Radio size='small'/>}
                                label={<Typography fontSize={'14px'}>{choice[`option${t('param')}`]}</Typography>}
                              />
                              {choice.subText && field.value?.selectedOptionGuid === choice.questionOptionGuid && (
                                <CustomTextField 
                                  value={field.value.subTextValue}
                                  onChange={(e) =>
                                    field.onChange({ ...field.value, subTextValue: e.target.value })
                                  }
                                  variant='standard'
                                  size='medium'
                                  placeholder={t('register-page.enterAnswer')}
                                />
                              )}
                            </div>
                          ))}
                        </RadioGroup>
                        {error &&
                          <FormHelperText variant='standard'>{error.message}</FormHelperText>
                        }
                      </FormControl>
                    )}
                  />
                )}
                </Grid2>
              </Grid2>
            ))}
            </Grid2>
          </Paper>
          }
          {data?.postFeeOptions?.length > 0 && 
          <Paper className='content-card'>
            <Grid2 container spacing={'10px'}>
              <Grid2 size={12}>
                <Grid2 size={12}>
                  <Controller
                    name={'postFeeOptionGuid'}
                    control={control}
                    defaultValue={""}
                    rules={{ required: t('register-page.pleaseSelectOption') }}
                    render={({ field, fieldState: { error } }) => (
                      <FormControl
                        error={error}
                        fullWidth
                      >
                        <FormLabel component="legend" style={{fontSize: '12px'}}>{t('register-page.selectAnOption')}</FormLabel>
                        <RadioGroup
                          {...field}
                        >
                          {data?.postFeeOptions && 
                            data?.postFeeOptions
                              .map((item, index) => (
                              <FormControlLabel
                                value={item?.postFeeOptionGuid}
                                control={<Radio size='small'/>}
                                label={<Typography fontSize={'14px'}>{item[`optionName${t('param')}`]} (RM{item.fee})</Typography>}
                                key={index}
                              />
                            ))
                          }
                        </RadioGroup>
                        {error &&
                          <FormHelperText variant='standard'>{error.message}</FormHelperText>
                        }
                      </FormControl>
                    )}
                  />
                </Grid2>
              </Grid2>
            </Grid2>
          </Paper>
          }
          <Paper className='content-card'>
            <Grid2 container spacing={'10px'}>
              {/* <Grid2 size={12}>
                <Grid2 size={12}>
                  <Controller
                    name={'isAgreeTerms'}
                    control={control}
                    defaultValue={false}
                    rules={{ required: t('register-page.agreeTermsRequired') }}
                    render={({ field, fieldState: { error } }) => (
                      <FormControl
                        error={error}
                        fullWidth
                      >
                        <FormGroup>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  {...field}
                                  checked={field.value}
                                  size='small'
                                />
                              }
                              label={
                                <Typography fontSize={'14px'}>
                                  {t('register-page.agreeTermsStart')}
                                  <Link component={RouterLink} to="/terms-and-conditions" target="_blank" rel="noopener noreferrer">{t('register-page.agreeTermsLink')}</Link>
                                  {t('register-page.agreeTermsEnd')}
                                </Typography>
                              }
                            />
                        </FormGroup>
                        {error &&
                          <FormHelperText variant='standard'>{error.message}</FormHelperText>
                        }
                      </FormControl>
                    )}
                  />
                </Grid2>
              </Grid2> */}
              {/* <Grid2 size={12}>
                <Grid2 size={12}>
                  <Controller
                    name={'isAgreeData'}
                    control={control}
                    defaultValue={false}
                    rules={{ required: t('register-page.agreeDataCollectionRequired') }}
                    render={({ field, fieldState: { error } }) => (
                      <FormControl
                        error={error}
                        fullWidth
                      >
                        <FormGroup>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  {...field}
                                  checked={field.value}
                                  size='small'
                                />
                              }
                              label={
                                <Typography fontSize={'14px'}>
                                  {t('register-page.agreeDataCollectionStart')}
                                  <Link component={RouterLink} to="/privacy-policy" target="_blank" rel="noopener noreferrer">{t('register-page.agreeDataCollectionLink')}</Link>
                                  {t('register-page.agreeDataCollectionEnd')}
                                </Typography>
                              }
                            />
                        </FormGroup>
                        {error &&
                          <FormHelperText variant='standard'>{error.message}</FormHelperText>
                        }
                      </FormControl>
                    )}
                  />
                </Grid2>
              </Grid2> */}
              <Grid2 size={12}>
                <Grid2 size={12}>
                  <Controller
                    name={'recevieNewsChecker'}
                    control={control}
                    defaultValue={false}
                    render={({ field, fieldState: { error } }) => (
                      <FormControl
                        error={error}
                        fullWidth
                      >
                        <FormGroup>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  {...field}
                                  checked={field.value}
                                  size='small'
                                />
                              }
                              label={
                                <Typography fontSize={'14px'}>
                                  {t('register-page.receiveNewsletter')}
                                </Typography>
                              }
                            />
                        </FormGroup>
                        {error &&
                          <FormHelperText variant='standard'>{error.message}</FormHelperText>
                        }
                      </FormControl>
                    )}
                  />
                </Grid2>
              </Grid2>
            </Grid2>
          </Paper>
          <Paper className='content-card'>
            <CustomButton type="submit" disabled={disabledSubmit}>{t('register-page.submit')}</CustomButton>
          </Paper>
        </form>
      }
      <ConfirmDialog 
        open={openConfirmDialog}
        onClose={()=>setOpenConfirmDialog(false)}
        onSubmit={onConfirmSubmit}
        dialogTitle={t('register-page.submitApplication')}
        dialogDescription={t('register-page.submitApplicationConfirmation')}
        cancelText={t('cancel')}
        confirmText={t('confirm')}
        loading={registerMutation.isPending}
      />
      <InfoDialog
        open={openInfoDialog}
        onClose={()=>{setOpenInfoDialog(false);navigate(`/activity/${id}`, {replace: true})}}
        dialogTitle={t('register-page.receivedApplication')}
        dialogDescription={t('register-page.receivedApplicationDesc')}
        closeText={t('ok')}
      />
    </Container>
  )
}
